// `/admin/ledger` (croqui `screens/admin/ledger.tsx`, `spec/17-frontend.md` §8.2).
// Foco: **a tabela de lançamentos**.
//
// Os filtros e o lançamento aberto vivem na query string (`spec/17` §3), não em
// `useState`: recarregar, voltar e compartilhar o link continuam valendo. `mode` é filtro
// de tela, o admin não tem toggle global (`spec/17` §4.4 e §13 item 4).
//
// O detalhe é `?entry=<id>` e não uma rota `$id`: `spec/24` §5 não lista rota filha para
// `/admin/ledger` e `files_allowed` desta task só tem este arquivo em `routes/`. Mesmo
// caminho da WEB-53 e da WEB-70, drawer com URL, deep link e voltar do navegador.

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { ADMIN_LEDGER_KINDS } from '../api/queries/admin-ledger.ts';
import { LedgerScreen, type LedgerSearch } from '../features/admin-ledger/ledger-screen.tsx';
import { regionOf, regionSearchField, regionSearchOf } from '../features/admin-region/region.tsx';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Query string tolerante a lixo: `?kind=banana` cai em `undefined` em vez de derrubar a rota. */
const searchSchema = z.object({
  seller_id: z.string().min(1).optional().catch(undefined),
  mode: z.enum(['live', 'test']).default('live').catch('live'),
  kind: z.enum(ADMIN_LEDGER_KINDS).optional().catch(undefined),
  from: z.string().regex(ISO_DATE).optional().catch(undefined),
  to: z.string().regex(ISO_DATE).optional().catch(undefined),
  entry: z.string().min(1).optional().catch(undefined),
  region: regionSearchField,
});

function Ledger() {
  const search = Route.useSearch();
  const navigate = useNavigate();

  const value: LedgerSearch = {
    sellerId: search.seller_id,
    mode: search.mode,
    region: regionOf(search.region),
    kind: search.kind,
    from: search.from,
    to: search.to,
    entry: search.entry,
  };

  const apply = (next: LedgerSearch): void => {
    void navigate({
      to: '/admin/ledger',
      search: {
        ...(next.sellerId === undefined ? {} : { seller_id: next.sellerId }),
        mode: next.mode,
        ...regionSearchOf(next.region),
        ...(next.kind === undefined ? {} : { kind: next.kind }),
        ...(next.from === undefined ? {} : { from: next.from }),
        ...(next.to === undefined ? {} : { to: next.to }),
        ...(next.entry === undefined ? {} : { entry: next.entry }),
      },
      // Abrir e fechar o detalhe são passos de navegação (o voltar do navegador fecha o
      // drawer); trocar filtro continua substituindo a entrada do histórico.
      replace: next.entry === search.entry,
    });
  };

  return <LedgerScreen search={value} onSearch={apply} />;
}

export const Route = createFileRoute('/admin/ledger/')({
  validateSearch: searchSchema,
  component: Ledger,
});
